import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { sendWelcomeEmail, updateOnboardingStatus } from "@/api/vercel";
import { storeAuthData } from "@/utils/auth";
import {
  codeWasSent,
  findCodeInText,
  getAuthWorkspace,
  maskEmail,
  requestSignInCode,
} from "@/utils/sign-in-code";
import { appendUtmsToPath } from "@/utils/utm";
import { landingAfterSignIn } from "@/utils/todays-tips-ready";
import Navbar from "@/components/navbar";

const CODE_LENGTH = 6;
// The page opens right after /login sent a code, so the first "Send a new code" waits too
const RESEND_COOLDOWN_MS = 30 * 1000;

export default function ConfirmSso() {
  const location = useLocation();
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  // Codes the server refused are never sent again on their own; one request at a time
  const [rejectedCodes, setRejectedCodes] = useState([]);
  const verifying = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [isEmailVerification, setIsEmailVerification] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [resendAt, setResendAt] = useState(
    () => Date.now() + RESEND_COOLDOWN_MS,
  );
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const codeRefused = rejectedCodes.includes(code);

  const handleVerify = useCallback(async () => {
    if (verifying.current || code.length !== CODE_LENGTH) return;
    verifying.current = true;
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    const verificationCode = code;
    // Get phone number from sessionStorage, prefer cleanPhoneNumber if available
    let phoneNumber =
      sessionStorage.getItem("cleanPhoneNumber") ||
      sessionStorage.getItem("phoneNumber");

    // Ensure phone number has + prefix (E.164 format)
    if (phoneNumber && !phoneNumber.startsWith("+")) {
      phoneNumber = "+" + phoneNumber;
    }

    const email = sessionStorage.getItem("tempEmail");
    const verificationToken = sessionStorage.getItem("verificationSid");

    // Check for plan query param
    const urlParams = new URLSearchParams(location.search);
    const planParam = urlParams.get("plan");
    if (
      planParam === "pro" ||
      planParam === "day-pass" ||
      planParam === "free"
    ) {
      sessionStorage.setItem("selectedPlan", planParam);
    }

    // Check for mode query param (md=signup indicates signup flow)
    const modeParam = urlParams.get("md");
    const isSignupMode = modeParam === "signup";

    try {
      // Use Vercel API on localhost, Cloud Function on deployed environments
      const hostname = window.location.hostname;

      let url, body;

      // Check the code in the backend that sent it (same hostname rule as the send call)
      url = `https://australia-southeast1-lexi-458204.cloudfunctions.net/lexi-${getAuthWorkspace(hostname)}-email-verification`;

      // Build the request body
      body = {
        action: "verify",
        email,
        otp: verificationCode,
      };

      // Only add mode field if this is a signup flow
      // The md query parameter must never be trusted for authorization
      // and should only be used to control client-side flow and payload shape
      if (isSignupMode) {
        body.mode = "signup";
        if (phoneNumber) body.phoneNumber = phoneNumber;
      }

      // Call the verification endpoint
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store email permanently and remove temporary storage
        sessionStorage.setItem("userEmail", email);
        sessionStorage.removeItem("tempEmail");
        sessionStorage.removeItem("verificationMethod");

        // Get verification SID
        const verificationSid = sessionStorage.getItem("verificationSid");

        // Store authentication data with 72-hour expiration
        // If we have a token from the backend, use it; otherwise store email-only auth
        if (data.token) {
          // Full authentication with JWT
          storeAuthData(verificationSid, email, phoneNumber, data.token, 72);
        } else {
          // Email-only verification (backend unavailable)
          // Store minimal auth data so user can proceed
          sessionStorage.setItem("emailVerified", "true");
          sessionStorage.setItem("verificationSid", verificationSid);
          localStorage.setItem(
            "sessionData",
            JSON.stringify({
              verificationSid: verificationSid,
              email: email,
              phoneNumber: phoneNumber,
              emailVerified: true,
              createdAt: new Date().toISOString(),
              expiresAt: new Date(
                Date.now() + 72 * 60 * 60 * 1000,
              ).toISOString(),
            }),
          );
        }

        if (isSignupMode) {
          // Send welcome email to the user after successful verification
          sendWelcomeEmail({
            email: email,
          }).catch((error) => {
            console.error("Failed to send welcome email:", error);
            // Don't block the flow if email fails
          });
        }

        setSuccessMessage("Email verified successfully!");

        // Check for plan query parameter
        const urlParams = new URLSearchParams(location.search);
        const planParam = urlParams.get("plan");

        // If user selected free plan, redirect directly to today's tips
        // (or /home until today's tips are out: landingAfterSignIn)
        if (planParam === "free") {
          if (data.customer?.id) {
            await updateOnboardingStatus(data.customer.id, false);
          }
          window.location.href = appendUtmsToPath(
            await landingAfterSignIn("/todays-tips"),
          );
          return;
        }

        // Check onboarding status from sessionStorage (extracted from token)
        const onboarding = sessionStorage.getItem("onboarding");
        const needsOnboarding = onboarding === "true";

        // Check if we have a redirect path
        const redirectPath = sessionStorage.getItem("redirectAfterLogin");

        if (needsOnboarding) {
          // Redirect to onboarding page if user needs to complete onboarding
          window.location.href = appendUtmsToPath("/onboard");
        } else if (redirectPath) {
          // Clear the redirect path from session storage
          sessionStorage.removeItem("redirectAfterLogin");
          // Redirect to the stored path (/home instead of /todays-tips until today's tips are out)
          window.location.href = appendUtmsToPath(
            await landingAfterSignIn(redirectPath),
          );
        } else {
          // Default redirect to today's tips page, or /home until today's tips are out
          window.location.href = appendUtmsToPath(
            await landingAfterSignIn("/todays-tips"),
          );
        }
      } else {
        // Only a refused code is remembered: a 429 or 5xx can be retried with Verify
        if (response.status === 401 || data.code === "invalid_otp") {
          setRejectedCodes((codes) => [...codes, verificationCode]);
          // Ready for the next try: typing or pasting replaces the refused code
          const input = inputRef.current;
          if (input && input.value === verificationCode) {
            input.focus();
            input.select();
          }
        }
        const errorMessage =
          data.message || data.error || "Verification failed";
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("Failed to verify code. Please try again.");
    } finally {
      verifying.current = false;
      setIsLoading(false);
    }
  }, [code]);

  // Verify as soon as six digits are in, unless the server already refused them
  useEffect(() => {
    if (code.length === CODE_LENGTH && !rejectedCodes.includes(code)) {
      handleVerify();
    }
  }, [code, rejectedCodes, handleVerify]);

  // Enter key / Verify button: never re-send a refused code
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!rejectedCodes.includes(code)) handleVerify();
  };

  // Paste the whole email line and keep just the code ("Your Lexi code is 482913. It expires in 10 minutes");
  // otherwise paste as usual, digits only
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData("text") ?? "";
    const input = e.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    e.preventDefault();
    setCode(
      findCodeInText(text) ??
        (input.value.slice(0, start) + text + input.value.slice(end))
          .replace(/\D/g, "")
          .slice(0, CODE_LENGTH),
    );
  };

  // Tick the "Send a new code in 25s" countdown while it runs
  const coolingDown = resendIn > 0;
  useEffect(() => {
    if (!coolingDown) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [coolingDown]);

  const handleResend = async () => {
    if (isResending || resendIn > 0) return;
    setError("");
    setSuccessMessage("");
    const email = sessionStorage.getItem("tempEmail");
    if (!email) {
      setError(
        "We couldn't find your email address. Go back to Login and enter it again.",
      );
      return;
    }
    setIsResending(true);
    try {
      const result = await requestSignInCode(email);
      if (codeWasSent(result)) {
        if (result.sid) sessionStorage.setItem("verificationSid", result.sid);
        setCode("");
        setRejectedCodes([]);
        setSuccessMessage(`New code sent to ${maskEmail(email)}`);
        const sentAt = Date.now();
        setNow(sentAt);
        setResendAt(sentAt + RESEND_COOLDOWN_MS);
        inputRef.current?.focus();
      } else {
        const reason = result.message || result.error;
        setError(
          reason
            ? `We couldn't send a new code: ${reason}`
            : "We couldn't send a new code. Please try again.",
        );
      }
    } catch (err) {
      console.error("Resend code error:", err);
      setError(
        "We couldn't send a new code. Check your connection and try again.",
      );
    } finally {
      setIsResending(false);
    }
  };

  // Check if we have a message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [location]);

  // Get and mask email on component mount
  useEffect(() => {
    // Check if this is email verification
    const verificationMethod = sessionStorage.getItem("verificationMethod");
    setIsEmailVerification(verificationMethod === "email" || true); // Default to email

    // Get email from sessionStorage
    const email = sessionStorage.getItem("tempEmail");

    // Show only the first 2 chars and the domain: jo***@gmail.com
    if (email) setMaskedEmail(maskEmail(email));
  }, []);

  return (
    <div className="min-h-screen flex flex-col pb-[95px] pt-[64px] max-w-md mx-auto bg-[#F5F5F5]">
      <Navbar />
      <div className="flex flex-col items-center px-4 flex-1 justify-center">
        <div className="w-20 h-20 bg-[#FCEDF7] rounded-full flex items-center justify-center mb-6">
          <svg
            className="w-10 h-10 text-[#F0A7D6]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold mb-2">Check Your Email</h1>
        <p id="code-hint" className="text-gray-600 mb-6 text-center">
          We sent a 6-digit code to {maskedEmail || "your email"}
        </p>

        <div role="status" className="w-full max-w-sm">
          {successMessage && (
            <p className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium text-center p-3 rounded-lg mb-4">
              {successMessage}
            </p>
          )}
        </div>

        <form noValidate onSubmit={handleSubmit} className="w-full max-w-sm">
          <label
            htmlFor="code"
            className="block text-sm font-semibold text-gray-900 mb-2"
          >
            Enter the 6-digit code
          </label>
          <div id="code-error" role="alert">
            {error && (
              <p className="bg-red-50 border border-red-200 text-red-700 text-sm font-medium p-3 rounded-lg mb-3">
                {error}
              </p>
            )}
          </div>
          <input
            ref={inputRef}
            id="code"
            type="text"
            name="code"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))
            }
            onPaste={handlePaste}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            aria-invalid={codeRefused || undefined}
            aria-describedby={error ? "code-error code-hint" : "code-hint"}
            autoFocus
            // tracking adds a gap after the last digit too; the matching left padding keeps the digits centred
            className={`block w-full h-14 mb-6 pl-[0.5em] text-center text-[28px] font-semibold tabular-nums tracking-[0.5em] rounded-lg border-2 bg-white text-gray-900 caret-brand-pink focus:outline-none focus:ring-2 focus:ring-brand-pink transition-colors duration-200 ${
              codeRefused ? "border-red-700" : "border-brand-pink"
            }`}
          />

          <button
            type="submit"
            className="w-full bg-brand-pink text-white py-3 rounded-lg font-medium hover:bg-[#e391c4] transition-all duration-200 disabled:opacity-50"
            disabled={isLoading || code.length !== CODE_LENGTH || codeRefused}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Verifying...
              </span>
            ) : (
              "Verify"
            )}
          </button>
        </form>

        <p className="mt-6 text-sm text-gray-600 text-center">
          Didn&apos;t get it?{" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || resendIn > 0}
            className="font-semibold text-[#C8007E] underline underline-offset-2 hover:text-brand-pink disabled:text-gray-600 disabled:no-underline disabled:cursor-not-allowed"
          >
            {isResending
              ? "Sending a new code..."
              : resendIn > 0
                ? `Send a new code in ${resendIn}s`
                : "Send a new code"}
          </button>
        </p>

        <Link
          to="/login"
          className="block mt-4 text-gray-600 hover:text-gray-800 transition-all duration-200"
        >
          Back to Login
        </Link>
      </div>
    </div>
  );
}
